import React, { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const DAY_RANGE = 7; // -7 ... +7

export interface DayInfo {
  index: number;       // 0..14 (7 = heute)
  offset: number;      // -7..+7
  label: string;       // "Heute" / "Gestern" / "Morgen" / "Fr 25.4."
  weekday: string;     // "Mo"
  dayNumber: number;   // 25
  monthShort: string;  // "Apr"
  date: Date;
}

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

export function buildDays(): DayInfo[] {
  const now = new Date();
  const result: DayInfo[] = [];
  for (let i = 0; i < DAY_RANGE * 2 + 1; i++) {
    const offset = i - DAY_RANGE;
    const date = new Date(now);
    date.setDate(date.getDate() + offset);
    const weekday = WEEKDAYS[date.getDay()] ?? '';
    const monthShort = MONTHS[date.getMonth()] ?? '';
    let label: string;
    if (offset === -1) label = 'Gestern';
    else if (offset === 0) label = 'Heute';
    else if (offset === 1) label = 'Morgen';
    else label = `${weekday} ${date.getDate()}.${date.getMonth() + 1}.`;
    result.push({
      index: i,
      offset,
      label,
      weekday,
      dayNumber: date.getDate(),
      monthShort,
      date,
    });
  }
  return result;
}

interface DaySelectorProps {
  days: DayInfo[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

/**
 * Sticky horizontale Tages-Navigation (-7 bis +7 Tage).
 * Pro Tag eine Kachel mit Wochentag, Tag-Nummer und Monat.
 */
const DaySelector: React.FC<DaySelectorProps> = ({ days, selectedIndex, onSelect }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Sanftes Auto-Scroll zum aktiven Tag
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const active = scroller.querySelector<HTMLElement>(`[data-day-index="${selectedIndex}"]`);
    if (active) {
      const scrollerRect = scroller.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();
      const delta =
        activeRect.left + activeRect.width / 2 - (scrollerRect.left + scrollerRect.width / 2);
      scroller.scrollBy({ left: delta, behavior: 'smooth' });
    }
  }, [selectedIndex]);

  const goTo = (index: number) => {
    if (index < 0 || index >= days.length) return;
    onSelect(index);
  };

  return (
    <div className="sticky top-16 z-30 bg-black/80 backdrop-blur-md border-b border-zinc-800/80">
      <div className="max-w-4xl mx-auto px-2 sm:px-4 py-2.5 flex items-center gap-1.5 sm:gap-2">
        <button
          onClick={() => goTo(selectedIndex - 1)}
          disabled={selectedIndex === 0}
          className="p-2 rounded-lg text-gray-400 hover:bg-zinc-800 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0"
          aria-label="Vorheriger Tag"
        >
          <ChevronLeft size={18} />
        </button>

        <div
          ref={scrollerRef}
          className="flex-1 flex gap-1.5 overflow-x-auto scroll-smooth py-0.5 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none' }}
        >
          {days.map((day) => {
            const selected = day.index === selectedIndex;
            const isToday = day.offset === 0;

            let tileCls =
              'bg-zinc-900/80 text-gray-400 border border-transparent hover:bg-zinc-800 hover:text-gray-200';
            if (selected) {
              tileCls =
                'bg-[#3fced1] text-black border border-[#3fced1] shadow-[0_0_18px_-2px_rgba(63,206,209,0.45)]';
            } else if (isToday) {
              tileCls = 'bg-zinc-900 text-[#3fced1] border border-[#3fced1]/40 hover:bg-zinc-800';
            }

            return (
              <button
                key={day.index}
                data-day-index={day.index}
                onClick={() => goTo(day.index)}
                aria-label={day.label}
                aria-current={selected ? 'date' : undefined}
                className={`flex flex-col items-center justify-center min-w-[60px] px-2.5 py-1.5 rounded-xl flex-shrink-0 transition-all duration-200 ${tileCls}`}
              >
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wider leading-none ${
                    selected ? 'text-black/70' : isToday ? 'text-[#3fced1]' : ''
                  }`}
                >
                  {isToday ? 'Heute' : day.weekday}
                </span>
                <span className="text-base font-bold leading-tight tabular-nums">
                  {day.dayNumber}
                </span>
                <span
                  className={`text-[10px] leading-none ${
                    selected ? 'text-black/70' : 'text-gray-500'
                  }`}
                >
                  {day.monthShort}
                </span>
              </button>
            );
          })}
        </div>

        <button
          onClick={() => goTo(selectedIndex + 1)}
          disabled={selectedIndex === days.length - 1}
          className="p-2 rounded-lg text-gray-400 hover:bg-zinc-800 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0"
          aria-label="Nächster Tag"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default DaySelector;
